import React from 'react';
import { Link } from 'react-router-dom';
import LegalPageShell from '../components/Legal/LegalPageShell';

const TermsOfService: React.FC = () => {
  return (
    <LegalPageShell title="Terms of Use">
      <p className="mb-3">
        <strong className="text-dark">Effective date:</strong> 04 April 2026
      </p>

      <h2 className="h5 fw-semibold text-dark mt-4 mb-3">1. Introduction</h2>
      <p>
        These Terms of Use (&quot;Terms&quot;) govern your access to and use of the Karen Ruimy mobile application (the
        &quot;App&quot;), related websites, content, products, services, events, and digital offerings provided by
        Karais Limited (&quot;Karais Limited&quot;, &quot;we&quot;, &quot;us&quot;, &quot;our&quot;).
      </p>
      <p>
        Karais Limited is a company registered in England and Wales under company number 06299901 with its registered
        office at 56 Grosvenor Street, London, W1K 3HZ, United Kingdom.
      </p>
      <p>
        By downloading, accessing, registering for, subscribing to, or using the App, you agree to be legally bound by
        these Terms.
      </p>
      <p>If you do not agree to these Terms, you must not use the App.</p>

      <h2 className="h5 fw-semibold text-dark mt-4 mb-3">2. Eligibility</h2>
      <p>You must be at least 16 years old to access or use the App.</p>
      <p>By using the App, you represent and warrant that:</p>
      <ul>
        <li>You are at least 16 years of age.</li>
        <li>You have the legal capacity to enter into a binding agreement.</li>
        <li>All information you provide is accurate and complete.</li>
        <li>You will keep your account information up to date.</li>
      </ul>
      <p>
        We reserve the right to suspend or terminate any account where we reasonably believe these requirements are not
        met.
      </p>

      <h2 className="h5 fw-semibold text-dark mt-4 mb-3">3. The Nature of the App</h2>
      <p>The Karen Ruimy App is a personal growth, wellness, spiritual, and transformational platform.</p>
      <p>The App may include:</p>
      <ul>
        <li>Guided meditations</li>
        <li>Transformational journeys</li>
        <li>Healing exercises</li>
        <li>Personal development programmes</li>
        <li>Oracle card guidance</li>
        <li>Manifestation tools</li>
        <li>Journaling tools</li>
        <li>Workshops and webinars</li>
        <li>Educational resources</li>
        <li>Ask Karen AI</li>
        <li>Audio, video, written, and digital content</li>
      </ul>
      <p>
        The App is intended for educational, personal development, self-reflection, inspirational, spiritual, wellbeing,
        and entertainment purposes only.
      </p>
      <p>
        The App is not a medical service, healthcare service, mental health service, counselling service, psychotherapy
        service, legal service, financial service, or professional advisory service.
      </p>

      <h2 className="h5 fw-semibold text-dark mt-4 mb-3">4. Accounts</h2>
      <p>You may be required to create an account to access certain features.</p>
      <p>You are responsible for:</p>
      <ul>
        <li>Maintaining the confidentiality of your login credentials.</li>
        <li>Restricting access to your account.</li>
        <li>All activities undertaken through your account.</li>
      </ul>
      <p>You agree to notify us immediately of any unauthorised use of your account.</p>
      <p>We reserve the right to suspend, restrict, or terminate accounts at our sole discretion.</p>

      <h2 className="h5 fw-semibold text-dark mt-4 mb-3">5. Subscriptions</h2>
      <p>Certain features of the App require a paid subscription.</p>
      <p>Current plans include:</p>
      <ul>
        <li>
          <strong className="text-dark">Monthly Plan</strong> — £14.99 per month
        </li>
        <li>
          <strong className="text-dark">Annual Plan</strong> — £125.99 per year
        </li>
      </ul>
      <p>Prices may be changed from time to time.</p>
      <p>Where legally required, notice of pricing changes will be provided before changes take effect.</p>

      <h2 className="h5 fw-semibold text-dark mt-4 mb-3">6. Free Trial</h2>
      <p>The App currently offers a 3-day free trial.</p>
      <p>
        Unless cancelled before the end of the trial period, the selected subscription will automatically convert into a
        paid subscription.
      </p>
      <p>We reserve the right to withdraw, modify, or restrict free trial offers at any time.</p>
      <p>Only one free trial may be permitted per user unless otherwise specified.</p>

      <h2 className="h5 fw-semibold text-dark mt-4 mb-3">7. Automatic Renewal</h2>
      <p>All subscriptions automatically renew unless cancelled.</p>
      <p>By subscribing, you authorise recurring payments using your selected payment method.</p>
      <p>Your subscription will continue until cancelled.</p>
      <p>You may cancel through:</p>
      <ul>
        <li>Apple App Store</li>
        <li>Google Play Store</li>
        <li>Account settings</li>
      </ul>
      <p>Cancellation prevents future charges but does not automatically generate refunds.</p>

      <h2 className="h5 fw-semibold text-dark mt-4 mb-3">8. Payments</h2>
      <p>Payments may be processed through:</p>
      <ul>
        <li>Apple App Store</li>
        <li>Google Play</li>
        <li>Stripe</li>
        <li>PayPal</li>
        <li>Other authorised payment providers</li>
      </ul>
      <p>You agree that all payment information supplied is accurate and valid.</p>
      <p>Failure to process payment may result in suspension or termination of access.</p>

      <h2 className="h5 fw-semibold text-dark mt-4 mb-3">9. Refunds</h2>
      <p>Except where required by law:</p>
      <ul>
        <li>Subscription fees are non-refundable.</li>
        <li>Partially used subscription periods are non-refundable.</li>
        <li>Unused subscriptions are non-refundable.</li>
      </ul>
      <p>Purchases made through Apple or Google may be subject to their own refund procedures.</p>

      <h2 className="h5 fw-semibold text-dark mt-4 mb-3">10. Ask Karen AI</h2>
      <p>
        The App may provide access to an artificial intelligence feature known as Ask Karen AI.
      </p>
      <p>
        Ask Karen AI provides responses generated using artificial intelligence technology and may utilise third-party
        providers.
      </p>
      <p>You acknowledge and agree that:</p>
      <ul>
        <li>Responses are generated by artificial intelligence.</li>
        <li>Responses may contain inaccuracies.</li>
        <li>Responses may be incomplete.</li>
        <li>Responses may not reflect Karen Ruimy&apos;s personal views.</li>
        <li>Responses should not be relied upon as factual, professional, or expert advice.</li>
      </ul>
      <p>
        Ask Karen AI is provided solely for educational, inspirational, self-development, reflective, and entertainment
        purposes.
      </p>
      <p>Ask Karen AI does not provide:</p>
      <ul>
        <li>Medical advice</li>
        <li>Mental health treatment</li>
        <li>Therapy</li>
        <li>Psychiatric advice</li>
        <li>Legal advice</li>
        <li>Financial advice</li>
        <li>Investment advice</li>
        <li>Relationship counselling</li>
      </ul>
      <p>You remain solely responsible for any decisions made based upon AI-generated responses.</p>

      <h2 className="h5 fw-semibold text-dark mt-4 mb-3">11. Journal Features</h2>
      <p>The App may allow you to create and store:</p>
      <ul>
        <li>Journal entries</li>
        <li>Reflections</li>
        <li>Personal goals</li>
        <li>Milestones</li>
        <li>Notes</li>
      </ul>
      <p>You retain ownership of your personal journal content.</p>
      <p>However, you grant us the limited rights necessary to:</p>
      <ul>
        <li>Store your content</li>
        <li>Synchronise your content</li>
        <li>Back up your content</li>
        <li>Display your content to you</li>
      </ul>
      <p>You are solely responsible for any information entered into your journal.</p>

      <h2 className="h5 fw-semibold text-dark mt-4 mb-3">12. Oracle Readings and Spiritual Guidance</h2>
      <p>The App may include:</p>
      <ul>
        <li>Oracle card readings</li>
        <li>Spiritual teachings</li>
        <li>Intuitive guidance</li>
        <li>Manifestation tools</li>
        <li>Energetic exercises</li>
        <li>Personal development content</li>
      </ul>
      <p>Such content is provided solely for:</p>
      <ul>
        <li>Personal reflection</li>
        <li>Inspiration</li>
        <li>Self-development</li>
        <li>Spiritual exploration</li>
        <li>Education</li>
        <li>Entertainment</li>
      </ul>
      <p>Oracle readings and spiritual guidance should not be interpreted as:</p>
      <ul>
        <li>Medical advice</li>
        <li>Legal advice</li>
        <li>Financial advice</li>
        <li>Investment advice</li>
        <li>Professional advice</li>
        <li>Predictions of future events</li>
      </ul>
      <p>You remain solely responsible for your actions, choices, and decisions.</p>

      <h2 className="h5 fw-semibold text-dark mt-4 mb-3">13. Entertainment Purposes</h2>
      <p>Certain content within the App, including but not limited to:</p>
      <ul>
        <li>Oracle readings</li>
        <li>Spiritual guidance</li>
        <li>Manifestation content</li>
        <li>AI-generated content</li>
        <li>Intuitive exercises</li>
        <li>Energetic practices</li>
      </ul>
      <p>may be considered entertainment content.</p>
      <p>
        Such content is intended to encourage reflection, personal exploration, inspiration, and enjoyment.
      </p>
      <p>
        It should not be relied upon as a substitute for independent judgement or professional advice.
      </p>

      <h2 className="h5 fw-semibold text-dark mt-4 mb-3">14. Manifestation Disclaimer</h2>
      <p>The App may reference concepts including:</p>
      <ul>
        <li>Manifestation</li>
        <li>Abundance</li>
        <li>Success</li>
        <li>Healing</li>
        <li>Relationships</li>
        <li>Self-worth</li>
        <li>Confidence</li>
        <li>Personal transformation</li>
      </ul>
      <p>These concepts represent personal development philosophies and individual beliefs.</p>
      <p>We make no guarantees regarding:</p>
      <ul>
        <li>Manifestation results</li>
        <li>Financial outcomes</li>
        <li>Relationship outcomes</li>
        <li>Business outcomes</li>
        <li>Healing outcomes</li>
        <li>Personal outcomes</li>
      </ul>
      <p>Individual experiences vary significantly.</p>

      <h2 className="h5 fw-semibold text-dark mt-4 mb-3">15. Wellness Disclaimer</h2>
      <p>The App is designed to support wellbeing and personal growth.</p>
      <p>Nothing contained within the App should be interpreted as:</p>
      <ul>
        <li>Medical treatment</li>
        <li>Mental health treatment</li>
        <li>Healthcare advice</li>
        <li>Psychiatric treatment</li>
        <li>Therapy</li>
        <li>Counselling</li>
        <li>Clinical diagnosis</li>
      </ul>
      <p>
        Always consult appropriately qualified professionals regarding health, mental health, legal, financial, or
        medical concerns.
      </p>

      <h2 className="h5 fw-semibold text-dark mt-4 mb-3">16. No Therapeutic Relationship</h2>
      <p>Your use of the App does not create:</p>
      <ul>
        <li>A therapist-client relationship</li>
        <li>A counselling relationship</li>
        <li>A doctor-patient relationship</li>
        <li>A coaching relationship</li>
        <li>A fiduciary relationship</li>
        <li>Any professional-client relationship</li>
      </ul>
      <p>
        Karen Ruimy and Karais Limited do not provide personalised therapy or healthcare services through the App.
      </p>

      <h2 className="h5 fw-semibold text-dark mt-4 mb-3">17. No Guarantee of Results</h2>
      <p>We do not guarantee any specific results from use of the App.</p>
      <p>We make no promises regarding:</p>
      <ul>
        <li>Emotional healing</li>
        <li>Personal growth</li>
        <li>Manifestation</li>
        <li>Relationships</li>
        <li>Business success</li>
        <li>Financial gain</li>
        <li>Wellbeing</li>
        <li>Spiritual development</li>
      </ul>
      <p>Results vary between individuals.</p>

      <h2 className="h5 fw-semibold text-dark mt-4 mb-3">18. User Conduct</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Violate laws</li>
        <li>Harass others</li>
        <li>Upload malicious software</li>
        <li>Attempt unauthorised access</li>
        <li>Reverse engineer the App</li>
        <li>Copy content</li>
        <li>Share subscriptions</li>
        <li>Misuse AI features</li>
        <li>Disrupt services</li>
      </ul>
      <p>We may suspend or terminate access for violations.</p>

      <h2 className="h5 fw-semibold text-dark mt-4 mb-3">19. Intellectual Property</h2>
      <p>All App content remains the property of Karais Limited or its licensors.</p>
      <p>This includes:</p>
      <ul>
        <li>Meditations</li>
        <li>Journeys</li>
        <li>Workshops</li>
        <li>Audio</li>
        <li>Video</li>
        <li>Graphics</li>
        <li>Branding</li>
        <li>Text</li>
        <li>Oracle content</li>
        <li>AI content</li>
        <li>Digital materials</li>
      </ul>
      <p>
        No content may be copied, distributed, reproduced, sold, modified, republished, or commercially exploited
        without written permission.
      </p>

      <h2 className="h5 fw-semibold text-dark mt-4 mb-3">20. User Feedback</h2>
      <p>
        Any feedback, ideas, recommendations, or suggestions provided by you may be used by us without restriction or
        compensation.
      </p>

      <h2 className="h5 fw-semibold text-dark mt-4 mb-3">21. Third-Party Services</h2>
      <p>The App may integrate with:</p>
      <ul>
        <li>OpenAI</li>
        <li>Apple</li>
        <li>Google</li>
        <li>Analytics providers</li>
        <li>Payment providers</li>
        <li>Webinar platforms</li>
        <li>Cloud hosting providers</li>
      </ul>
      <p>We are not responsible for third-party services or their actions.</p>

      <h2 className="h5 fw-semibold text-dark mt-4 mb-3">22. App Availability</h2>
      <p>We do not guarantee uninterrupted access.</p>
      <p>The App may be unavailable due to:</p>
      <ul>
        <li>Maintenance</li>
        <li>Technical issues</li>
        <li>Security incidents</li>
        <li>Third-party outages</li>
        <li>Updates</li>
      </ul>
      <p>We shall not be liable for temporary unavailability.</p>

      <h2 className="h5 fw-semibold text-dark mt-4 mb-3">23. Modifications to the App</h2>
      <p>We reserve the right to:</p>
      <ul>
        <li>Modify features</li>
        <li>Remove features</li>
        <li>Introduce new features</li>
        <li>Change content</li>
        <li>Change subscription offerings</li>
        <li>Discontinue services</li>
      </ul>
      <p>at any time without liability.</p>

      <h2 className="h5 fw-semibold text-dark mt-4 mb-3">24. Account Termination</h2>
      <p>We may suspend or terminate your access if:</p>
      <ul>
        <li>You breach these Terms.</li>
        <li>We suspect fraud.</li>
        <li>We suspect misuse.</li>
        <li>We are legally required to do so.</li>
        <li>We discontinue the App.</li>
      </ul>
      <p>Termination may occur without notice.</p>

      <h2 className="h5 fw-semibold text-dark mt-4 mb-3">25. Limitation of Liability</h2>
      <p>To the fullest extent permitted by law:</p>
      <p>
        Karais Limited, Karen Ruimy, employees, contractors, affiliates, officers, and representatives shall not be
        liable for:
      </p>
      <ul>
        <li>Indirect losses</li>
        <li>Consequential losses</li>
        <li>Emotional distress</li>
        <li>Business losses</li>
        <li>Lost profits</li>
        <li>Lost opportunities</li>
        <li>Data loss</li>
        <li>Reliance on AI content</li>
        <li>Reliance on spiritual guidance</li>
        <li>Reliance on manifestation content</li>
        <li>Reliance on oracle readings</li>
      </ul>
      <p>
        Our total liability shall not exceed the total subscription fees paid by you during the twelve months preceding
        the event giving rise to the claim.
      </p>
      <p>Nothing in these Terms excludes liability which cannot legally be excluded.</p>

      <h2 className="h5 fw-semibold text-dark mt-4 mb-3">26. Indemnity</h2>
      <p>You agree to indemnify and hold harmless Karais Limited and its affiliates from claims arising from:</p>
      <ul>
        <li>Your misuse of the App</li>
        <li>Breach of these Terms</li>
        <li>Violation of applicable law</li>
        <li>Content submitted by you</li>
      </ul>

      <h2 className="h5 fw-semibold text-dark mt-4 mb-3">27. Privacy</h2>
      <p>
        Your use of the App is governed by our{' '}
        <Link to="/privacy" className="text-decoration-none">
          Privacy Policy
        </Link>
        .
      </p>
      <p>By using the App, you consent to the collection and processing of information as described therein.</p>

      <h2 className="h5 fw-semibold text-dark mt-4 mb-3">28. Force Majeure</h2>
      <p>
        We shall not be liable for delays or failures caused by events beyond our reasonable control including:
      </p>
      <ul>
        <li>Natural disasters</li>
        <li>Power outages</li>
        <li>Internet disruptions</li>
        <li>Cyber attacks</li>
        <li>Government actions</li>
        <li>War</li>
        <li>Pandemic events</li>
        <li>Third-party service failures</li>
      </ul>

      <h2 className="h5 fw-semibold text-dark mt-4 mb-3">29. International Users</h2>
      <p>The App is available worldwide.</p>
      <p>Users are responsible for complying with local laws applicable in their jurisdiction.</p>
      <p>Access to the App may not be lawful in all countries.</p>

      <h2 className="h5 fw-semibold text-dark mt-4 mb-3">30. Changes to These Terms</h2>
      <p>We may update these Terms from time to time.</p>
      <p>Updated Terms will be posted within the App and on our websites.</p>
      <p>Continued use of the App constitutes acceptance of revised Terms.</p>

      <h2 className="h5 fw-semibold text-dark mt-4 mb-3">31. Severability</h2>
      <p>
        If any provision of these Terms is found invalid or unenforceable, the remaining provisions shall remain in full
        force and effect.
      </p>

      <h2 className="h5 fw-semibold text-dark mt-4 mb-3">32. Entire Agreement</h2>
      <p>
        These Terms constitute the entire agreement between you and Karais Limited regarding use of the App and supersede
        any prior agreements.
      </p>

      <h2 className="h5 fw-semibold text-dark mt-4 mb-3">33. Governing Law</h2>
      <p>These Terms are governed by the laws of England and Wales.</p>
      <p>
        Any dispute arising under these Terms shall be subject to the exclusive jurisdiction of the courts of England
        and Wales except where mandatory consumer protection laws provide otherwise.
      </p>

      <h2 className="h5 fw-semibold text-dark mt-4 mb-3">34. Contact Us</h2>
      <p>
        <strong className="text-dark">Karais Limited</strong>
        <br />
        56 Grosvenor Street
        <br />
        London
        <br />
        W1K 3HZ
        <br />
        United Kingdom
      </p>
      <p>
        <strong className="text-dark">Email:</strong>{' '}
        <a href="mailto:team@karenruimy.com" className="text-decoration-none">
          team@karenruimy.com
        </a>
      </p>
      <p>
        <strong className="text-dark">Website:</strong>{' '}
        <a href="https://www.karenruimy.com" className="text-decoration-none" target="_blank" rel="noopener noreferrer">
          www.karenruimy.com
        </a>
      </p>
    </LegalPageShell>
  );
};

export default TermsOfService;
